import React from "react";
import clsx from "clsx";

import { ReactComponent as IconCheckOn } from "../../../assets/images/AnswerList/icon_check_on.svg";
import { ReactComponent as IconCheckOff } from "../../../assets/images/AnswerList/icon_check_off.svg";
import { ReactComponent as IconArrow } from "../../../assets/images/AnswerList/icon_arrow.svg";
import { ReactComponent as IconUser } from "../../../assets/images/AnswerList/icon_user.svg";
import {
  IAnswersByClass,
  IStudent,
  IClass,
  IGroup,
  ITeacherR,
  IAnswersFromOtherSchoolByClass,
  IAnswer,
} from "../../../state";
import { IAnswersByGroup } from "../../../pages/teacher/types";
import { generateFullName } from "../../../libs/Util";
import ModalDeleteConfirm from "../../../molecules/ModalDeleteConfirm";
import { getConfirmModalMessage } from "../../../libs/domUtils";
import {
  Context,
  BULK_CREATE_GROUPS_SUCCESS_MESSAGE,
} from "../../../teacherStore";
import AnswerBox from "../../../molecules/teacher/AnswerBox";
import OneAnswerContents from "../../../molecules/OneAnswerContents";
import ModalEditGroupClass from "../ModalEditGroupClass";

import Dropdown, { Option } from "./Dropdown";
import SwitchBox from "./SwitchBox";
import styles from "./AnswerList.module.scss";

export type AnswerProps = {
  calling: boolean;
  currentTeacherID: number;
  students: IStudent[];
  answersByClasses: IAnswersByClass[];
  answersOtherSchoolsByClasses: IAnswersFromOtherSchoolByClass[];
  classes: IClass[];
  classIDs: number[];
  groups: IGroup[];
  is_display_all_schools_answer: boolean;
  handleComment: (
    content: string,
    answerID: number,
    comment_id?: number,
  ) => void;
  handleDeleteComment: (answer_id: number, comment_id: number) => void;
  handleToggleComment: (
    answer_id: number,
    comment_id: number,
    is_hidden: boolean,
  ) => void;
  changeAnswerOpenClose: (class_id: number, opened: boolean) => void;
  changeNameOpenClose: (class_id: number, opened: boolean) => void;
};

const SORT_TYPE = {
  NewestAnswer: 1,
  ContentLength: 2,
  LikeCount: 3,
  StudentNumber: 4,
};

const OPTIONS: Option[] = [
  { label: "回答の新しい順", value: SORT_TYPE.NewestAnswer },
  { label: "文字数の多い順", value: SORT_TYPE.ContentLength },
  { label: "いいね数の多い順", value: SORT_TYPE.LikeCount },
  { label: "学籍番号順", value: SORT_TYPE.StudentNumber },
];

const DEFAULT_VALUE = OPTIONS[0];

const getContentsLength = (answer: IAnswer) =>
  answer.contents?.map(c => c.content.length).reduce((p, c) => p + c) ?? 0;

const AnswerList: React.FC<AnswerProps> = ({
  calling,
  currentTeacherID,
  students,
  answersByClasses,
  answersOtherSchoolsByClasses,
  classes,
  classIDs,
  groups,
  is_display_all_schools_answer,
  handleComment,
  handleDeleteComment,
  handleToggleComment,
  changeAnswerOpenClose,
  changeNameOpenClose,
}) => {
  const { state } = React.useContext(Context);
  const scrollRefs = React.useRef<any[]>([]);
  const highlightTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const [openModal, setOpenModal] = React.useState<{
    state: boolean;
    type: "delete" | "hide" | "group" | "edit";
    answerId: number;
    commentId: number;
    hiddenBy?: null | ITeacherR;
  }>({
    state: false,
    type: "delete",
    answerId: 0,
    commentId: 0,
  });
  const [listStudent, setListStudent] = React.useState<{
    [key: number]: IStudent[];
  }>({});

  const [sortType, setSortType] = React.useState(DEFAULT_VALUE);
  const [highlighted, setHighlighted] = React.useState<number | null>(null);

  const answersByStudents = React.useMemo(() => {
    const all = answersByClasses
      .map(k => k.answers.map(a => a.student.id))
      .flat();
    const studentIds = Array.from(new Set([...all]));
    return studentIds.map(studentId => {
      const klass = answersByClasses.find(k =>
        k.answers.find(a => a.student.id === studentId),
      )!;
      const answer = klass.answers.find(a => a.student.id === studentId)!;

      return {
        student_id: studentId,
        name_opened: klass.name_opened,
        answers: [answer],
        classFullName: klass.full_name,
      };
    });
  }, [answersByClasses]);

  const sortAnswersByStudentNo = (
    answersByStudents: {
      student_id: number;
      name_opened: boolean;
      answers: IAnswer[];
      classFullName: string;
    }[],
    students: IStudent[],
  ) => {
    const collator = new Intl.Collator(["ja", "en"], { numeric: true });

    const studentNoMap = new Map<number, string>();
    for (const s of students) {
      studentNoMap.set(s.id, s.student_no_in_school);
    }

    return [...answersByStudents].sort((x, y) => {
      const a = studentNoMap.get(x.student_id) ?? "";
      const b = studentNoMap.get(y.student_id) ?? "";

      if (!a) return 1;
      if (!b) return -1;

      return collator.compare(a, b);
    });
  };

  const sortedAnswersByStudents = React.useMemo(() => {
    switch (sortType.value) {
      case SORT_TYPE.ContentLength:
        // Sort by contents-length desc
        return [...answersByStudents].sort(
          (x, y) =>
            getContentsLength(y.answers[0]) - getContentsLength(x.answers[0]),
        );
      case SORT_TYPE.LikeCount:
        // Sort by like_num desc
        return [...answersByStudents].sort(
          (x, y) => y.answers[0].like_num - x.answers[0].like_num,
        );
      case SORT_TYPE.StudentNumber:
        // Sort by student_no_in_school desc
        return sortAnswersByStudentNo(answersByStudents, students);

      default:
        // Sort by id desc
        return [...answersByStudents].sort(
          (x, y) => y.answers[0].id - x.answers[0].id,
        );
    }
  }, [answersByStudents, sortType, students]);

  const answersByGroups: IAnswersByGroup[] = React.useMemo(
    () =>
      groups.map(group => {
        const abs = answersByStudents.filter(st =>
          st.answers.find(a => a.student.group?.id === group.id),
        );
        return {
          ...group,
          name_opened: abs.some(st => st.name_opened),
          studentAnswers: abs.map(st => st.answers),
        };
      }),
    [groups, answersByStudents],
  );

  React.useEffect(() => {
    if (
      state.success_message &&
      state.success_message !== BULK_CREATE_GROUPS_SUCCESS_MESSAGE
    ) {
      setOpenModal({
        state: false,
        type: "delete",
        answerId: 0,
        commentId: 0,
      });
    }
  }, [state.success_message]);

  React.useEffect(() => {
    const studentMap = {} as { [key: number]: IStudent[] };
    const studentsSortedByName = students.sort((a, b) => {
      return a.last_name_kana.localeCompare(b.last_name_kana, "ja");
    });
    studentsSortedByName.forEach(student => {
      studentMap[student.class_id]
        ? studentMap[student.class_id].push(student)
        : (studentMap[student.class_id] = [student]);
    });
    setListStudent(studentMap);
  }, [students]);

  const scrollSmoothHandler = (index: number) => {
    scrollRefs.current[index]?.current?.scrollIntoView({ behavior: "smooth" });
  };

  sortedAnswersByStudents.forEach((_, i) => {
    scrollRefs.current[i] = React.createRef();
  });

  const handleClickStudent = (student_id: number) => {
    const index = sortedAnswersByStudents.findIndex(
      st => st.student_id === student_id,
    );
    if (index >= 0) {
      scrollSmoothHandler(index);
      setHighlighted(index);
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = setTimeout(() => setHighlighted(null), 1500);
    }
  };

  React.useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  const isShowClassFullName = answersByClasses.length > 1;

  const getGroupStudentTotal = (id: number) => {
    return students.filter(student => student.group_id === id).length;
  };

  const getStudentNoInSchoolFrom = (studentId: number) => {
    return (
      students.find(student => student.id === studentId)
        ?.student_no_in_school ?? 0
    );
  };

  const hasMultipleClasses =
    new Set(answersByGroups.map(g => g.klass.id)).size > 1;

  const modalMessage = getConfirmModalMessage({
    type: openModal.type,
    hiddenBy: openModal.hiddenBy,
  });

  const handleModalConfirm = () => {
    if (openModal.type === "delete") {
      handleDeleteComment(openModal.answerId, openModal.commentId);
    }

    if (openModal.type === "hide") {
      handleToggleComment(
        openModal.answerId,
        openModal.commentId,
        !openModal.hiddenBy,
      );
    }

    setOpenModal({
      state: false,
      type: "delete",
      answerId: 0,
      commentId: 0,
    });
  };

  const hasClosedNameInAnswersByClasses = answersByClasses.some(
    answer => !answer.name_opened,
  );

  const availableSortOptions = hasClosedNameInAnswersByClasses
    ? OPTIONS.filter(option => option.value !== SORT_TYPE.StudentNumber)
    : OPTIONS;

  const handleChangeNameOpenClose = (class_id: number, opened: boolean) => {
    if (!opened && sortType.value === SORT_TYPE.StudentNumber) {
      setSortType(DEFAULT_VALUE);
    }

    changeNameOpenClose(class_id, opened);
  };

  return (
    <div className="d-flex h-100">
      <div className={clsx(styles.body, styles.wrapper__modal)}>
        <ul
          className={clsx(
            `nav nav-pills`,
            styles.tabs,
            is_display_all_schools_answer && styles.otherSchool,
          )}
          id="pills-tab"
          role="tablist"
        >
          <li className="nav-item">
            <a
              className="active"
              data-bs-toggle="pill"
              href="#pills-class"
              role="tab"
              aria-controls="pills-class"
              aria-selected="true"
            >
              クラスの回答
            </a>
          </li>
          <li className="nav-item">
            <a
              data-bs-toggle="pill"
              href="#pills-group"
              role="tab"
              aria-controls="pills-group"
              aria-selected="false"
            >
              グループの回答
            </a>
          </li>
          {is_display_all_schools_answer && (
            <li className="nav-item">
              <a
                data-bs-toggle="pill"
                href="#pills-other-school"
                role="tab"
                aria-controls="pills-other-school"
                aria-selected="false"
              >
                他校の回答
              </a>
            </li>
          )}
        </ul>
        <div className="tab-content" id="pills-tabContent">
          <div
            className={`tab-pane fade show active`}
            id="pills-class"
            role="tabpanel"
            aria-labelledby="pills-class-tab"
          >
            <div className={styles.classes}>
              {classIDs.map(clId => {
                const answersByClass = answersByClasses.find(
                  k => k.id === clId,
                );
                return (
                  <div className={styles.controller} key={clId}>
                    <div className={styles.head}>
                      <button
                        className={styles.toggleButton}
                        data-bs-toggle="collapse"
                        aria-expanded="true"
                        data-bs-target={`#listStudent${clId}`}
                      >
                        <IconArrow className={styles.iconArrow} />
                        <p className={styles.class}>
                          {classes.find(cls => clId === cls.id)?.full_name}
                        </p>
                      </button>
                      {answersByClass && (
                        <div className={styles.switchGroup}>
                          <div className={styles.switchBox}>
                            <SwitchBox
                              label="みんなの回答を公開"
                              type="answer_opened"
                              answersByClass={answersByClass}
                              onChange={changeAnswerOpenClose}
                            />
                          </div>
                          <div className={styles.switchBox}>
                            <SwitchBox
                              label="生徒名をクラスに公開"
                              type="name_opened"
                              answersByClass={answersByClass}
                              onChange={handleChangeNameOpenClose}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="collapse show" id={`listStudent${clId}`}>
                      <div className={styles.studentList}>
                        {!answersByClass?.name_opened &&
                        answersByClass?.answers.length === 0 ? (
                          <p className={styles.emptyMessage}>
                            まだ回答がありません。
                          </p>
                        ) : answersByClass?.name_opened ? (
                          listStudent[clId]?.map(
                            student =>
                              student.class_id === clId && (
                                <div
                                  className={styles.studentItem}
                                  key={student.id}
                                  data-bs-tooltip={`${generateFullName([
                                    student.last_name,
                                    student.first_name,
                                  ])}\n${generateFullName([
                                    student.last_name_kana,
                                    student.first_name_kana,
                                  ])}`}
                                  onClick={() => handleClickStudent(student.id)}
                                >
                                  <div className={styles.studentContent}>
                                    {answersByClass.answers.find(
                                      answer =>
                                        answer.student.id === student.id,
                                    ) ? (
                                      <IconCheckOn className={styles.icon} />
                                    ) : (
                                      <IconCheckOff className={styles.icon} />
                                    )}
                                    <div className={styles.fullName}>
                                      <span className={styles.name}>
                                        {generateFullName([
                                          student.last_name,
                                          student.first_name,
                                        ])}
                                      </span>
                                      <span className={styles.kana}>
                                        {generateFullName([
                                          student.last_name_kana,
                                          student.first_name_kana,
                                        ])}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ),
                          )
                        ) : (
                          answersByClass?.answers.map(a => (
                            <div
                              key={a.student.id}
                              className={styles.studentItem}
                              onClick={() => handleClickStudent(a.student.id)}
                            >
                              <p className={styles.name}>回答{a.index}</p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="d-flex align-items-center justify-content-end mt-4">
              <Dropdown
                className={styles.dropdown}
                options={availableSortOptions}
                defaultValue={sortType}
                onChange={value => {
                  const selectSort = availableSortOptions.find(
                    option => option.value === value,
                  ) as Option;
                  setSortType(selectSort);
                }}
              />
            </div>
            <div className={clsx(styles.answerList, "mt-4")}>
              {sortedAnswersByStudents.length > 0 ? (
                sortedAnswersByStudents.map((st, index) => (
                  <AnswerBox
                    key={`answer-${st.student_id}`}
                    calling={calling}
                    studentNoInSchool={String(
                      getStudentNoInSchoolFrom(st.student_id),
                    )}
                    studentAnswers={st.answers}
                    currentTeacherID={currentTeacherID}
                    name_opened={st.name_opened}
                    classFullName={st.classFullName}
                    isShowClassFullName={isShowClassFullName}
                    isHighlighted={highlighted === index}
                    handleComment={handleComment}
                    handleToggleComment={handleToggleComment}
                    handleDeleteComment={handleDeleteComment}
                    ref={scrollRefs.current[index]}
                  />
                ))
              ) : (
                <p className={styles.emptyMessageBox}>まだ回答がありません。</p>
              )}
            </div>
          </div>
          <div
            className={clsx(styles.group, "tab-pane", "fade")}
            id="pills-group"
            role="tabpanel"
            aria-labelledby="pills-group-tab"
          >
            {groups.length > 0 ? (
              <>
                <div className={styles.meta}>
                  <span className={styles.totalGroup}>
                    合計：{groups.length}グループ
                  </span>
                  <button
                    className={styles.groupEditButton}
                    onClick={() => {
                      setOpenModal({
                        state: true,
                        type: "group",
                        answerId: 0,
                        commentId: 0,
                      });
                    }}
                  >
                    グループを編集する
                  </button>
                </div>
                <div className={styles.groupList}>
                  {answersByGroups.map(group => {
                    const total = getGroupStudentTotal(group.id);
                    const answered = group.studentAnswers.length;

                    return (
                      <React.Fragment key={group.id}>
                        <div
                          className={styles.controller}
                          style={{ cursor: "pointer" }}
                          data-bs-toggle="collapse"
                          data-bs-target={`#group${group.id}`}
                          aria-expanded="false"
                          aria-controls={`group${group.id}`}
                        >
                          <div className={styles.head}>
                            <button className={styles.toggleButton}>
                              <IconArrow className={styles.iconArrow} />
                              {hasMultipleClasses && (
                                <span className={styles.class}>
                                  {group.klass.full_name}
                                </span>
                              )}
                              <span className={styles.group}>{group.name}</span>
                            </button>
                            <span className={styles.answerCount}>
                              {total === 0 ? "0" : `${answered}/${total}`}人回答
                            </span>
                          </div>
                        </div>
                        <div className={"collapse"} id={`group${group.id}`}>
                          <div className={styles.groupItem}>
                            {answered > 0 ? (
                              group.studentAnswers.map((answers, index) => (
                                <AnswerBox
                                  key={`groupAnswer${group.id}-${index}`}
                                  isGroupAnswer
                                  calling={calling}
                                  studentNoInSchool={String(
                                    getStudentNoInSchoolFrom(
                                      answers[0].student.id,
                                    ),
                                  )}
                                  studentAnswers={answers}
                                  currentTeacherID={currentTeacherID}
                                  name_opened={group.name_opened}
                                  handleComment={handleComment}
                                  handleToggleComment={handleToggleComment}
                                  handleDeleteComment={handleDeleteComment}
                                />
                              ))
                            ) : (
                              <p className={styles.emptyMessageBox}>
                                まだ回答がありません。
                              </p>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className={styles.groupItem}>
                <p className={styles.emptyMessage}>
                  まだグループがありません。
                </p>
                <div className={styles.buttonGroup}>
                  <button
                    className={styles.groupCreateButton}
                    onClick={() => {
                      setOpenModal({
                        state: true,
                        type: "group",
                        answerId: 0,
                        commentId: 0,
                      });
                    }}
                  >
                    グループを作る
                  </button>
                </div>
                <p className={styles.emptyMessageBox}>まだ回答がありません。</p>
              </div>
            )}
          </div>
          <div
            className={clsx(styles.otherSchool, "tab-pane", "fade")}
            id="pills-other-school"
            role="tabpanel"
            aria-labelledby="pills-group-tab"
          >
            <div className={styles.answerList}>
              {answersOtherSchoolsByClasses.length > 0 ? (
                answersOtherSchoolsByClasses.map(cl =>
                  cl.answers.map(answer => (
                    <div
                      className={styles.answerFromOtherSchool}
                      key={answer.id}
                    >
                      <p className={styles.name}>
                        <IconUser />
                        {cl.grade >= 1 && cl.grade <= 3
                          ? `${cl.grade}年生`
                          : "その他"}
                      </p>
                      <div className={styles.content}>
                        <OneAnswerContents
                          contents={answer.contents}
                          hideBorder
                        />
                      </div>
                    </div>
                  )),
                )
              ) : (
                <p className={styles.emptyMessageBox}>まだ回答がありません。</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {openModal.state && openModal.type === "delete" && (
        <ModalDeleteConfirm
          title={modalMessage}
          onConfirm={handleModalConfirm}
          onClose={() =>
            setOpenModal({
              state: false,
              type: "delete",
              answerId: 0,
              commentId: 0,
            })
          }
        />
      )}
      {openModal.state && openModal.type === "group" && (
        <ModalEditGroupClass
          classIDs={classIDs}
          onClose={() =>
            setOpenModal({
              state: false,
              type: "delete",
              answerId: 0,
              commentId: 0,
            })
          }
        />
      )}
    </div>
  );
};

export default AnswerList;
